import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../hooks/useTheme';
import { apiFetch } from '../services/api';
import { toast } from '../hooks/useToast';
import {
  X, Download, Moon, Contrast, Sun,
  Tag, Plus, Pencil, Trash2,
} from 'lucide-react';

const CATEGORY_ICONS = [
  '🍔', '🚗', '🏠', '💊', '🎮', '✈️', '🛒', '💼',
  '📚', '🐾', '👕', '💸', '🍺', '⚡', '💰', '📱',
  '💻', '🎁', '🚂', '🎧', '🏋️', '🌟', '🎪', '💡',
  '🔧', '☕', '🍕', '💈', '🎨', '📌',
];

interface BudgetCategory {
  id: number;
  name: string;
  color: string;
  icon?: string;
}

interface SettingsModalProps {
  onClose: () => void;
}

export function SettingsModal({ onClose }: SettingsModalProps) {
  const { theme, setTheme, themes } = useTheme();
  const [exporting, setExporting] = useState(false);
  const [categories, setCategories] = useState<BudgetCategory[]>([]);
  const [showCatForm, setShowCatForm] = useState(false);
  const [editingCatId, setEditingCatId] = useState<number | null>(null);
  const [catName, setCatName] = useState('');
  const [catColor, setCatColor] = useState('#6366f1');
  const [catIcon, setCatIcon] = useState('');

  const iconMap: Record<string, typeof Moon> = { moon: Moon, contrast: Contrast, sun: Sun };

  const loadCategories = useCallback(async () => {
    try {
      const res = await apiFetch('/api/budget/categories');
      const data = await res.json();
      if (data.success) setCategories(data.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const exportData = async () => {
    setExporting(true);
    try {
      const [nRes, tRes, bRes] = await Promise.all([
        apiFetch('/api/notes'),
        apiFetch('/api/tasks'),
        apiFetch('/api/budget/entries?from=2020-01-01&to=2099-12-31'),
      ]);
      const [notes, tasks, budget] = await Promise.all([nRes.json(), tRes.json(), bRes.json()]);
      const dump = {
        exported_at: new Date().toISOString(),
        notes: notes.data || [],
        tasks: tasks.data || [],
        budget_entries: budget.data || [],
      };
      const blob = new Blob([JSON.stringify(dump, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dailylab-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast('Данные экспортированы');
    } catch {
      toast('Ошибка экспорта', 'error');
    }
    setExporting(false);
  };

  const saveCategory = async () => {
    if (!catName.trim()) return;
    if (editingCatId) {
      await apiFetch(`/api/budget/categories/${editingCatId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: catName.trim(), color: catColor, icon: catIcon }),
      });
      toast('Категория обновлена');
    } else {
      await apiFetch('/api/budget/categories', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: catName.trim(), color: catColor, icon: catIcon }),
      });
      toast('Категория добавлена');
    }
    resetCatForm();
    await loadCategories();
  };

  const deleteCategory = async (id: number) => {
    if (!confirm('Удалить категорию?')) return;
    await apiFetch(`/api/budget/categories/${id}`, { method: 'DELETE' });
    toast('Категория удалена');
    await loadCategories();
  };

  const editCategory = (c: BudgetCategory) => {
    setEditingCatId(c.id);
    setCatName(c.name);
    setCatColor(c.color);
    setCatIcon(c.icon || '');
    setShowCatForm(true);
  };

  const resetCatForm = () => {
    setShowCatForm(false);
    setEditingCatId(null);
    setCatName(''); setCatColor('#6366f1'); setCatIcon('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto shadow-2xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 sticky top-0 bg-slate-900 z-10">
          <h2 className="text-lg font-semibold text-white">Настройки</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Theme */}
          <div>
            <h3 className="text-sm font-medium text-slate-300 mb-3">Тема оформления</h3>
            <div className="flex gap-2">
              {themes.map((t) => {
                const Icon = iconMap[t.icon];
                return (
                  <button
                    key={t.id}
                    onClick={() => setTheme(t.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${
                      theme === t.id
                        ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/20'
                        : 'bg-slate-800 text-slate-500 hover:text-white hover:bg-slate-700'
                    }`}
                  >
                    <Icon size={18} />
                    <span className="text-sm">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Export */}
          <div>
            <h3 className="text-sm font-medium text-slate-300 mb-3">Экспорт данных</h3>
            <button
              onClick={exportData}
              disabled={exporting}
              className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 transition-all disabled:opacity-50"
            >
              <Download size={18} />
              {exporting ? 'Экспортируется...' : 'Скачать JSON-файл'}
            </button>
          </div>

          {/* Budget Categories */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-slate-300">Категории бюджета</h3>
              <button
                onClick={() => { resetCatForm(); setShowCatForm(true); }}
                className="flex items-center gap-1 px-2.5 py-1 bg-indigo-600 hover:bg-indigo-500 text-white text-xs rounded-lg transition-colors"
              >
                <Plus size={12} /> Добавить
              </button>
            </div>

            {showCatForm && (
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-3 mb-3 space-y-3 animate-slide-down">
                <div className="flex gap-3">
                  <input value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="Название"
                    className="flex-1 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50" autoFocus />
                  <input type="color" value={catColor} onChange={(e) => setCatColor(e.target.value)} className="w-9 h-9 rounded-lg cursor-pointer border-0 shrink-0" />
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-lg border" style={{ borderColor: `${catColor}60`, background: `${catColor}20` }}>
                    {catIcon || <span className="text-slate-600 text-xs">?</span>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {CATEGORY_ICONS.map((ic) => (
                    <button key={ic} onClick={() => setCatIcon(ic)}
                      className={`text-base w-8 h-8 flex items-center justify-center rounded-lg transition-all ${catIcon === ic ? 'ring-2 ring-indigo-500 bg-indigo-500/20' : 'bg-slate-800 hover:bg-slate-700'}`}>
                      {ic}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={saveCategory} className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-lg transition-colors">
                    {editingCatId ? 'Сохранить' : 'Создать'}
                  </button>
                  <button onClick={resetCatForm} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors">
                    Отмена
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {categories.map((c) => (
                <div key={c.id} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800/40 hover:bg-slate-800/70 group transition-colors">
                  {c.icon && <span className="text-base">{c.icon}</span>}
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ background: c.color }} />
                  <span className="text-sm text-slate-300 flex-1 truncate">{c.name}</span>
                  <button onClick={() => editCategory(c)} className="p-1 text-slate-600 hover:text-white opacity-0 group-hover:opacity-100 transition-all">
                    <Pencil size={12} />
                  </button>
                  <button onClick={() => deleteCategory(c.id)} className="p-1 text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              {categories.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">Нет категорий</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
